import { set } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

// Zoned results are returned as UTC ISO strings rather than Date objects. Every widget in the picker
// resolves a string against the selected timezone (Calendar's utcToWallClockDate, DateInputPicker's
// parseDateValue, TimeSelector's formatDate), but a bare Date is ambiguous — Calendar passes Dates
// straight through and renders them in the *browser's* zone, so the calendar highlighted a different
// day than the date inputs showed for the very same value.
const combineDateWithTime = (dateSource, timeSource, timezone) => {
  if (timezone) {
    const d = toZonedTime(new Date(dateSource), timezone);
    const t = toZonedTime(new Date(timeSource), timezone);
    const combined = set(d, {
      hours: t.getHours(),
      minutes: t.getMinutes(),
      seconds: t.getSeconds()
    });
    return fromZonedTime(combined, timezone).toISOString();
  }

  const d = new Date(dateSource);
  const t = new Date(timeSource);
  return set(d, { hours: t.getHours(), minutes: t.getMinutes(), seconds: t.getSeconds() });
};

const setDefaultTimes = (baseDate, hours, minutes, timezone) => {
  if (timezone) {
    const zoned = toZonedTime(new Date(baseDate), timezone);
    return fromZonedTime(set(zoned, { hours, minutes, seconds: 0 }), timezone).toISOString();
  }
  return set(new Date(baseDate), { hours, minutes, seconds: 0 });
};

// A date-only value ('2026-06-02', '06/02/2026') carries no time, so it must not be used as a time
// source: instant-converting it yields the previous day at 21:00 in any zone west of the browser, and
// that bogus 21:00 then gets baked into the emitted range — a "June 8" filter would start at 9 PM and
// miss the whole day. Treated as absent so the whole-day defaults below apply instead.
const DATE_ONLY_LIKE_RE = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4})$/;
const hasTimeComponent = (v) => !!v && !(typeof v === 'string' && DATE_ONLY_LIKE_RE.test(v));

export const combineDateTime = (
  dateVal,
  timeVal,
  isDateRange = false,
  isTimeRange = false,
  timezone = null
) => {
  if (!dateVal && !timeVal) return null;

  // Special case: time range with single date (not date range)
  // dateVal = single date (e.g., Sept 22), timeVal = [startTime, endTime]
  if (isTimeRange && !isDateRange) {
    const baseDate = dateVal;
    const times = timeVal || [null, null];

    // If we have a base date but no usable times, create default times (00:00 - 23:59)
    if (baseDate && !hasTimeComponent(times[0]) && !hasTimeComponent(times[1])) {
      return [
        setDefaultTimes(baseDate, 0, 0, timezone),
        setDefaultTimes(baseDate, 23, 59, timezone)
      ];
    }

    const combinedStart =
      baseDate && hasTimeComponent(times[0])
        ? combineDateWithTime(baseDate, times[0], timezone)
        : baseDate || times[0];

    const combinedEnd =
      baseDate && hasTimeComponent(times[1])
        ? combineDateWithTime(baseDate, times[1], timezone)
        : baseDate || times[1];

    return [combinedStart, combinedEnd];
  }

  // Date range or both date range and time range
  if (isDateRange || isTimeRange) {
    const dates = dateVal || [null, null];
    const times = timeVal || [null, null];

    // If we have dates but no usable times, create default times (00:00 - 23:59)
    if (dates[0] && !hasTimeComponent(times[0]) && !hasTimeComponent(times[1])) {
      return [
        setDefaultTimes(dates[0], 0, 0, timezone),
        setDefaultTimes(dates[1] || dates[0], 23, 59, timezone)
      ];
    }

    const combinedStart =
      dates[0] && hasTimeComponent(times[0])
        ? combineDateWithTime(dates[0], times[0], timezone)
        : dates[0] || times[0];

    const combinedEnd =
      dates[1] && hasTimeComponent(times[1])
        ? combineDateWithTime(dates[1], times[1], timezone)
        : dates[1] || times[1];

    return [combinedStart, combinedEnd];
  }

  // Single date with optional time
  if (dateVal && hasTimeComponent(timeVal)) {
    return combineDateWithTime(dateVal, timeVal, timezone);
  }

  return dateVal || timeVal;
};
